/* import __COLOCATED_TEMPLATE__ from './index.hbs'; */
import Component from '@glimmer/component';
import { cached, tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import { debounceTask } from 'ember-lifeline';
import { addDays } from 'date-fns/addDays';
import { isSameDay } from 'date-fns/isSameDay';
import { startOfDay } from 'date-fns/startOfDay';
import { waitFor } from '@ember/test-waiters';
import type { ModelRegistry } from 'ember-data/types/registries/model';
import { formatDateQuery } from 'uplisting-frontend/utils/date';
import { arrayOf } from 'uplisting-frontend/utils/utils';
import type { GenericChangeset } from 'uplisting-frontend/services/repositories/-base';
import { getSummaryRouteFor } from 'uplisting-frontend/utils/rhs/routes';

interface IArgs {
  booking: ModelRegistry['booking'];
}

export interface BookingModifyV1Signature {
  Element: HTMLDivElement;

  Args: IArgs;
}

export default class UiBookingSummaryModifyV1Component extends Component<BookingModifyV1Signature> {
  @service store!: Services['store'];
  @service notifications!: Services['notifications'];
  @service router!: Services['router'];

  @service('repositories/booking')
  bookingRepository!: Services['repositories/booking'];

  @service('repositories/airbnb-official-alteration-request')
  airbnbOfficialAlterationRequestRepository!: Services['repositories/airbnb-official-alteration-request'];

  @tracked isUpdatingPrice = false;
  @tracked isInvalid = false;
  @tracked isDatesOverlapError = false;
  @tracked isSubmitting = false;
  @tracked showConfirmationModal = false;
  @tracked newQuote: ModelRegistry['quote'] | null = null;

  numberOfGuestsOptions = arrayOf(this.booking.property.maximumCapacity ?? 30);

  @cached
  get booking(): ModelRegistry['booking'] {
    return this.args.booking;
  }

  @cached
  get changeset(): GenericChangeset<ModelRegistry['booking']> {
    return this.bookingRepository.buildChangeset(this.booking);
  }

  @cached
  get showBreakdown(): boolean {
    if (this.booking.price.numberOfGuests !== this.selectedNumberOfGuests) {
      return true;
    }

    const isSameCheckIn = isSameDay(
      this.booking.checkIn,
      this.changeset.checkIn,
    );
    const isSameCheckOut = isSameDay(
      this.booking.checkOut,
      this.changeset.checkOut,
    );

    return !isSameCheckIn || !isSameCheckOut;
  }

  @cached
  get guestPriceDiff(): number {
    return (this.newQuote?.grossRevenue || 0) - this.booking.price.grossRevenue;
  }

  @cached
  get guestPriceDiffAbs(): number {
    return Math.abs(this.guestPriceDiff);
  }

  @cached
  get isGuestDiffPositive(): boolean {
    return this.guestPriceDiff >= 0;
  }

  @cached
  get isHostDiffPositive(): boolean {
    return this.hostPriceDiff >= 0;
  }

  @cached
  get hostPriceDiff(): number {
    const newHostEarnings = this.newQuote?.hostEarnings || 0;
    const currentHostEarnings = this.booking.price.hostEarnings || 0;

    return newHostEarnings - currentHostEarnings;
  }

  @cached
  get hostPriceDiffAbs(): number {
    return Math.abs(this.hostPriceDiff);
  }

  @cached
  get selectedNumberOfGuests(): number | null {
    return this.newQuote?.numberOfGuests || this.booking.price.numberOfGuests;
  }

  @cached
  get isSubmitDisabled(): boolean {
    return (
      this.isInvalid ||
      this.isUpdatingPrice ||
      !this.showBreakdown ||
      this.isSubmitting
    );
  }

  @cached
  get summaryRoute(): string {
    return getSummaryRouteFor(this.router.currentRouteName ?? '');
  }

  @cached
  get isMonthly(): boolean {
    return (this.router.currentRouteName ?? '').includes('monthly');
  }

  @cached
  get checkInMinDate(): Date {
    return startOfDay(new Date());
  }

  @cached
  get checkOutMinDate(): Date {
    return addDays(new Date(this.changeset.checkIn), 1);
  }

  @action
  handleDateRangeSelect(date1: Date, date2: Date): void {
    this.changeset.checkIn = formatDateQuery(date1);
    this.changeset.checkOut = formatDateQuery(date2);
    debounceTask(this, 'syncPrice', 250);
  }

  @action
  handleDateSelect(field: 'checkIn' | 'checkOut', date: Date): void {
    this.changeset[field] = formatDateQuery(date);
    debounceTask(this, 'syncPrice', 250);
  }

  @action
  handleNumberOfGuestsChange(numberOfGuests: number): void {
    this.changeset.price.numberOfGuests = numberOfGuests;

    debounceTask(this, 'syncPrice', 250);
  }

  @action
  onReturn(): void {
    const route = this.summaryRoute;
    if (!route) {
      return;
    }

    this.router.transitionTo(route, this.booking.id);
  }

  @action
  onShowModal(): void {
    if (!this.showBreakdown) {
      return;
    }

    this.showConfirmationModal = true;
  }

  @action
  onHideModal(): void {
    this.showConfirmationModal = false;
  }

  @action
  @waitFor
  async onSubmit(): Promise<void> {
    if (this.isSubmitDisabled) {
      return;
    }

    this.isSubmitting = true;

    const request = this.airbnbOfficialAlterationRequestRepository.createRecord(
      {
        booking: this.booking,
        startDate: this.changeset.checkIn,
        endDate: this.changeset.checkOut,
        numberOfGuests: this.changeset.price.numberOfGuests,
      },
    );

    try {
      await request.save();

      this.notifications.info('action_bookings_summary_modify.success');
      this.onReturn();
    } catch {
      this.notifications.error();
    } finally {
      this.isSubmitting = false;
    }
  }

  private async syncPrice(): Promise<void> {
    const { changeset, booking } = this;
    const { price } = changeset;

    this.isUpdatingPrice = true;
    this.isDatesOverlapError = false;
    this.isInvalid = false;

    const charges = price.charges.map((item) => item.toObject());
    const discounts = price.discounts.map((item) => item.toObject());

    const quote = this.store.createRecord('quote', {
      booking,
      charges,
      discounts,
      property: booking.property,
      checkIn: changeset.checkIn,
      checkOut: changeset.checkOut,
      cleaningFee: price.cleaningFee,
      numberOfGuests: price.numberOfGuests,
    });

    try {
      await quote.save();

      this.newQuote = quote.toObject();

      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (e: any) {
      const message = e?.errors[0]?.detail || '';
      const isDatesOverlapError = message.includes('dates are not available');

      if (isDatesOverlapError) {
        this.isDatesOverlapError = true;
      } else {
        this.notifications.error(message, true);
      }

      this.newQuote = null;
      this.isInvalid = true;
    } finally {
      this.store.unloadRecord(quote);

      this.isUpdatingPrice = false;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Ui::Booking::Summary::Modify::V1': typeof UiBookingSummaryModifyV1Component;
  }
}
